/* Enhances the native Horizon form. Variant requests, validation and cart writes remain native. */
const initialized = new WeakSet();
// Normalize only known external widget accessibility defects on product pages.
function fixWidgetAccessibility() {
  for (const viewport of document.querySelectorAll('meta[name="viewport"]')) {
    const value = 'width=device-width,initial-scale=1,viewport-fit=cover';
    if (viewport.content !== value) viewport.content = value;
  }
  const merchant = document.querySelector('#merchantwidgetiframe');
  if (merchant && !merchant.title) merchant.title = 'Google Merchant Information';
  const review = document.querySelector('#trustshop-no-review--button');
  if (review && review.getAttribute('aria-label') !== review.textContent.trim()) review.setAttribute('aria-label',review.textContent.trim());
}
let accessibilityQueued = false;
function queueAccessibility() {
  if (accessibilityQueued) return;
  accessibilityQueued = true;
  requestAnimationFrame(()=>{accessibilityQueued=false;fixWidgetAccessibility()});
}
function initialize(root) {
  if (initialized.has(root)) return;
  initialized.add(root);
  const section = root.closest('.shopify-section');
  const sticky = section?.querySelector('.ww-pdp-sticky');
  const touched = new Set();
  let ready = root.dataset.explicitVariant === 'true' || !root.querySelector('.variant-picker__form fieldset,.variant-option--dropdowns');
  const groups = () => [...root.querySelectorAll('.variant-picker__form fieldset,.variant-option--dropdowns')];
  const label = () => groups().length === 1 ? `Choose ${groups()[0].querySelector('legend,label')?.textContent.trim() || 'an option'}` : 'Choose Options';
  const purchaseButtons = () => [...root.querySelectorAll('button[name="add"]'), ...section.querySelectorAll('sticky-add-to-cart button[ref="addToCartButton"]')];
  function ensureZoomButton() {
    const container = root.querySelector('media-gallery slideshow-container');
    const zoom = root.querySelector('zoom-dialog');
    if (!container || !zoom || container.querySelector('.ww-pdp-zoom')) return;
    const button = document.createElement('button');
    button.type = 'button'; button.className = 'ww-pdp-zoom';
    button.textContent = 'Enlarge Image +'; button.setAttribute('aria-haspopup','dialog');
    button.addEventListener('click',async event=>{
      await customElements.whenDefined('zoom-dialog');
      const slides = [...container.querySelectorAll('slideshow-slide')];
      const index = Math.max(0,slides.findIndex(slide=>slide.getAttribute('aria-hidden')!=='true'));
      await zoom.open(index,event);
    });
    container.append(button);
  }
  function sync() {
    ensureZoomButton();
    for (const list of root.querySelectorAll('.slideshow-controls__thumbnails,.dialog-thumbnails-list')) {
      list.setAttribute('role','tablist'); list.setAttribute('aria-label','Product Images');
      for (const tab of list.querySelectorAll('button')) {
        tab.setAttribute('role','tab');
        tab.tabIndex = tab.getAttribute('aria-selected') === 'true' ? 0 : -1;
      }
    }
    groups().forEach((group,index)=>group.classList.toggle('ww-confirmed-option',touched.has(index)));
    root.classList.toggle('ww-needs-choice', !ready);
    sticky?.classList.toggle('ww-needs-choice', !ready);
    for (const button of purchaseButtons()) {
      if (!ready) button.setAttribute('aria-disabled','true');
      else button.removeAttribute('aria-disabled');
      let prompt = button.querySelector('.ww-choice-label');
      if (!prompt) { prompt = document.createElement('span'); prompt.className='ww-choice-label'; button.append(prompt); }
      const next = label();
      if (prompt.textContent !== next) prompt.textContent = next;
      if (!ready) button.setAttribute('aria-label',next);
      else button.removeAttribute('aria-label');
    }
    const status = root.querySelector('.ww-pdp-availability');
    const nativeButton = root.querySelector('button[name="add"]');
    if (status) {
      const value = !ready ? label() : nativeButton?.disabled ? 'Currently unavailable' : 'Available to order';
      if (status.textContent !== value) status.textContent=value;
    }
  }
  // Listen before native handlers: an unconfirmed default must never be purchased.
  section.addEventListener('click', event => {
    if (ready || !(event.target instanceof Element)) return;
    if (!event.target.closest('button[name="add"],sticky-add-to-cart button[ref="addToCartButton"]')) return;
    event.preventDefault(); event.stopImmediatePropagation();
    const picker = root.querySelector('variant-picker');
    picker?.scrollIntoView({block:'center',behavior:'smooth'});
    picker?.querySelector('input,select')?.focus({preventScroll:true});
  }, true);
  section.addEventListener('submit', event => {
    if (!ready && event.target.closest('product-form-component')) { event.preventDefault(); event.stopImmediatePropagation(); sync(); }
  }, true);
  function confirmOption(event) {
    const group = event.target.closest('fieldset,.variant-option--dropdowns');
    if (!group || !group.closest('variant-picker')) return;
    touched.add(groups().indexOf(group));
    ready = groups().every((_, index) => touched.has(index));
    sync();
  }
  root.addEventListener('change', confirmOption, true);
  root.addEventListener('click',event=>{if(event.target.matches('variant-picker input[type="radio"]')) confirmOption(event)},true);
  root.addEventListener('keydown',event=>{
    if (!(event.target instanceof Element) || event.target.getAttribute('role')!=='tab') return;
    const tabs = [...event.target.closest('[role="tablist"]').querySelectorAll('[role="tab"]')];
    const index = tabs.indexOf(event.target);
    const next = event.key==='ArrowRight' ? (index+1)%tabs.length : event.key==='ArrowLeft' ? (index-1+tabs.length)%tabs.length : event.key==='Home' ? 0 : event.key==='End' ? tabs.length-1 : null;
    if(next===null)return;event.preventDefault();tabs[next].focus();tabs[next].click();
  });
  let syncQueued = false;
  const observer = new MutationObserver(()=>{
    if (syncQueued) return;
    syncQueued = true;
    requestAnimationFrame(()=>{syncQueued=false;sync()});
  });
  observer.observe(root,{subtree:true,childList:true,attributes:true,attributeFilter:['disabled','aria-selected']});
  if (sticky) observer.observe(sticky,{subtree:true,childList:true,attributes:true,attributeFilter:['disabled']});
  let queued = false;
  let footerVisible = false;
  const footer = document.querySelector('footer');
  if (footer) new IntersectionObserver(entries=>{
    footerVisible = entries[0].isIntersecting;
    queueVisibility();
  }).observe(footer);
  function visibility() {
    queued = false;
    const dialogOpen = !!document.querySelector('dialog[open],.cart-drawer[open],cart-drawer-component dialog[open]');
    sticky?.classList.toggle('ww-overlay-hidden',dialogOpen || !!footerVisible);
  }
  function queueVisibility(){if(!queued){queued=true;requestAnimationFrame(visibility)}}
  new MutationObserver(queueVisibility).observe(document.body,{subtree:true,attributes:true,attributeFilter:['open']});
  sync(); visibility();
  fixWidgetAccessibility();
  new MutationObserver(queueAccessibility).observe(document.documentElement,{subtree:true,childList:true,attributes:true,attributeFilter:['content','title','aria-label']});
}
document.querySelectorAll('.ww-pdp').forEach(initialize);
document.addEventListener('shopify:section:load',event=>event.target.querySelectorAll('.ww-pdp').forEach(initialize));
